/**
 * Chat Assistant — Cognito token access.
 *
 * Integration guide §1: "Use the Cognito access token, not the ID token. The
 * server validates the token with Cognito GetUser, which accepts the access
 * token. An ID token will be rejected."
 *
 * This is deliberately separate from `core/axios/axios.ts`, which sends the
 * **ID** token to the REST API under the `authorizationToken` header. The two
 * transports authenticate with different tokens; do not share a cache.
 */

import { fetchAuthSession } from "aws-amplify/auth";

/** Claims this client cares about. Cognito sends many more. */
export interface AccessTokenClaims {
    /** `access` for an access token, `id` for an ID token. The guide requires `access`. */
    token_use?: string;
    /** App client the token was issued to — present on access tokens only. */
    client_id?: string;
    /** `aud` appears on ID tokens instead of `client_id`. */
    aud?: string;
    /** Issuer, e.g. `https://cognito-idp.<region>.amazonaws.com/<poolId>`. */
    iss?: string;
    sub?: string;
    username?: string;
    scope?: string;
    /** Expiry, seconds since epoch. */
    exp?: number;
    /** Issued at, seconds since epoch. */
    iat?: number;
}

export interface AccessTokenReport {
    present: boolean;
    /** True only when the token is an access token that has not expired. */
    valid: boolean;
    /** Human-readable reason when `valid` is false. */
    problem?: string;
    claims?: AccessTokenClaims;
    expiresAt?: Date;
    secondsRemaining?: number;
}

/** Decode a JWT payload without verifying the signature (the server verifies). */
export function decodeJwtPayload(token: string): AccessTokenClaims | null {
    try {
        const payload = token.split(".")[1];
        if (!payload) return null;
        // JWTs use base64url; restore standard base64 before decoding.
        const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
        const json = decodeURIComponent(
            atob(padded)
                .split("")
                .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join(""),
        );
        return JSON.parse(json);
    } catch {
        return null;
    }
}

/**
 * Classify a token against the guide's §1 requirement.
 *
 * Kept pure so the rule is unit-testable: an ID token, an expired token and a
 * malformed token are each rejected with a distinct reason.
 */
export function describeAccessToken(token: string | null): AccessTokenReport {
    if (!token) return { present: false, valid: false, problem: "No Cognito session." };

    const claims = decodeJwtPayload(token);
    if (!claims) return { present: true, valid: false, problem: "Token is not a decodable JWT." };

    if (claims.token_use !== "access") {
        return {
            present: true,
            valid: false,
            // The exact failure the guide warns about.
            problem: `Wrong token type: token_use="${claims.token_use ?? "unknown"}". The chat assistant requires the access token; an ID token is rejected by the server.`,
            claims,
        };
    }

    const expiresAt = claims.exp ? new Date(claims.exp * 1000) : undefined;
    const secondsRemaining = claims.exp ? Math.round(claims.exp - Date.now() / 1000) : undefined;

    if (secondsRemaining !== undefined && secondsRemaining <= 0) {
        return {
            present: true,
            valid: false,
            problem: "Access token has expired.",
            claims,
            expiresAt,
            secondsRemaining,
        };
    }

    return { present: true, valid: true, claims, expiresAt, secondsRemaining };
}

/** Dev-only guard: a wrong token type here fails the connection silently otherwise. */
function assertAccessToken(token: string | null): void {
    if (process.env.NODE_ENV === "production" || !token) return;
    const report = describeAccessToken(token);
    if (!report.valid) {
        // eslint-disable-next-line no-console
        console.error(`[chat-assistant] ${report.problem}`, {
            token_use: report.claims?.token_use,
            client_id: report.claims?.client_id ?? report.claims?.aud,
            iss: report.claims?.iss,
        });
    }
}

/**
 * Current Cognito **access** token, or null when there is no session.
 *
 * Amplify caches the session and refreshes it transparently when it is close to
 * expiry, so the common path costs nothing.
 */
export async function getAccessToken(): Promise<string | null> {
    try {
        const session = await fetchAuthSession();
        const token = session.tokens?.accessToken?.toString() ?? null;
        assertAccessToken(token);
        return token;
    } catch {
        return null;
    }
}

/**
 * Force Cognito to mint a fresh access token.
 *
 * Guide §10/§12: when a connect attempt is rejected before the socket opens, the
 * token is bad or expired — refresh it before retrying rather than replaying the
 * same rejected credential.
 */
export async function refreshAccessToken(): Promise<string | null> {
    try {
        const session = await fetchAuthSession({ forceRefresh: true });
        const token = session.tokens?.accessToken?.toString() ?? null;
        assertAccessToken(token);
        return token;
    } catch {
        return null;
    }
}

/**
 * Inspect the token the chat assistant would connect with right now.
 *
 * Returns claims and a verdict, never the token itself — guide §12 keeps the
 * credential out of logs and diagnostics.
 */
export async function inspectAccessToken(): Promise<AccessTokenReport> {
    return describeAccessToken(await getAccessToken());
}
